// Supabase Wrapper with Queue Integration
// Wraps existing Supabase calls with retry logic and fallbacks

import { queueSupabaseRequest } from './supabaseQueue';
import { supabase, getSupabaseUrl, getSupabaseAnonKey, isSupabaseConfigured } from '../config/supabase';

/**
 * Bearer token for a PostgREST request.
 *
 * This used to always send the anon key, which meant every request through this
 * wrapper reached Postgres as the `anon` role even when an admin was signed in,
 * and auth.uid() was always NULL. Any RLS policy that distinguished
 * authenticated users from anonymous ones would therefore have denied
 * everything, so the only policies that could work were fully permissive ones -
 * which is exactly what the audit found.
 *
 * Sending the session's access token instead lets Postgres see who is asking:
 * the request runs as `authenticated` with auth.uid() set, and falls back to the
 * anon key for logged-out visitors, who still need to read public content.
 *
 * NOTE: on its own this changes nothing about what is permitted - it only makes
 * the caller's identity visible so that RLS policies can be written at all.
 */
const getAuthToken = async () => {
  const anonKey = getSupabaseAnonKey();
  try {
    if (!supabase) return anonKey;
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || anonKey;
  } catch (err) {
    console.warn('⚠️ Could not read session, falling back to anon key:', err.message);
    return anonKey;
  }
};

/**
 * Standard headers for a direct PostgREST fetch.
 *
 * Several services (calendarService, appDataService, appLevelSettings,
 * surveyVotesService) call fetch directly instead of going through this
 * wrapper, and each used to inline the anon key as the bearer. That left them
 * running as `anon` even for a signed-in admin, so every one of their writes -
 * calendar weeks, scheduled movies, movie status, week visibility, app settings
 * and ALL survey voting - would be refused the moment RLS was enabled.
 *
 * Use this instead of hand-rolling the header block.
 *
 *   headers: await getSupabaseAuthHeaders({ Prefer: 'return=representation' })
 */
export const getSupabaseAuthHeaders = async (extra = {}) => ({
  apikey: getSupabaseAnonKey(),
  Authorization: `Bearer ${await getAuthToken()}`,
  'Content-Type': 'application/json',
  ...extra
});

// Enhanced fetch wrapper with queue integration
export const queuedSupabaseFetch = async (url, options = {}, fallbackFn = null, description = 'Supabase request') => {
  if (!isSupabaseConfigured()) {
    if (fallbackFn) {
      console.log('⚠️ Supabase not configured, using fallback');
      return fallbackFn();
    }
    throw new Error('Supabase not configured and no fallback provided');
  }

  const supabaseUrl = getSupabaseUrl();
  const supabaseKey = getSupabaseAnonKey();

  // Prepare the request function
  const requestFn = async () => {
    const fullUrl = url.startsWith('http') ? url : `${supabaseUrl}${url}`;

    // Resolved per attempt, so a token refreshed between retries is picked up.
    const authToken = await getAuthToken();

    const requestOptions = {
      ...options,
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    const response = await fetch(fullUrl, requestOptions);
    
    if (!response.ok) {
      let errorDetails;
      try {
        const errorText = await response.text();
        
        // Check if this is a duplicate key error (409 conflict with specific error code)
        let isDuplicateKey = false;
        try {
          const errorJson = JSON.parse(errorText);
          isDuplicateKey = response.status === 409 && 
                          errorJson.code === '23505' && 
                          errorJson.message && 
                          errorJson.message.includes('duplicate key value violates unique constraint');
          errorDetails = errorJson;
        } catch (parseError) {
          errorDetails = errorText;
        }
        
        // Only log errors for non-duplicate-key issues
        if (!isDuplicateKey) {
          console.error('Supabase API Error Details:');
          console.error('Status:', response.status);
          console.error('Status Text:', response.statusText);
          console.error('URL:', fullUrl);
          console.error('Request Method:', requestOptions.method);
          console.error('Request Headers:', JSON.stringify(requestOptions.headers, null, 2));
          console.error('Request Body:', requestOptions.body);
          console.error('Error Response:', errorText);
          
          if (typeof errorDetails === 'object') {
            console.error('Parsed Error JSON:', JSON.stringify(errorDetails, null, 2));
          } else {
            console.error('Error response is not JSON:', errorText);
          }
        } else {
          // For duplicate key errors, just log a simple message
          console.log('🔄 Duplicate key detected, will be handled by application logic');
        }
        
        throw new Error(`HTTP ${response.status}: ${typeof errorDetails === 'object' ? JSON.stringify(errorDetails) : errorDetails}`);
      } catch (textError) {
        console.error('Failed to read error response:', textError);
        throw new Error(`HTTP ${response.status}: Failed to read error response`);
      }
    }

    // Handle different response types
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    } else {
      return response.text();
    }
  };

  // Queue the request with fallback
  return queueSupabaseRequest(requestFn, {
    description,
    fallback: fallbackFn,
    priority: options.priority || 0,
    timeout: options.timeout || 10000
  });
};

// Wrapper for GET requests
export const queuedGet = async (endpoint, fallbackFn = null, description = 'GET request') => {
  return queuedSupabaseFetch(endpoint, { method: 'GET' }, fallbackFn, description);
};

// Wrapper for POST requests  
export const queuedPost = async (endpoint, data, fallbackFn = null, description = 'POST request') => {
  return queuedSupabaseFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Prefer': 'return=representation'
    }
  }, fallbackFn, description);
};

// Wrapper for PATCH requests
export const queuedPatch = async (endpoint, data, fallbackFn = null, description = 'PATCH request') => {
  return queuedSupabaseFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      'Prefer': 'return=minimal'
    }
  }, fallbackFn, description);
};

// Wrapper for DELETE requests
export const queuedDelete = async (endpoint, fallbackFn = null, description = 'DELETE request') => {
  return queuedSupabaseFetch(endpoint, { method: 'DELETE' }, fallbackFn, description);
};

// High-level wrapper for common operations
export const queuedSupabaseOperation = {
  // Select with fallback to localStorage
  select: async (table, query = '', fallbackKey = null, description = null) => {
    const endpoint = `/rest/v1/${table}${query ? '?' + query : ''}`;
    const desc = description || `SELECT from ${table}`;
    
    const fallback = fallbackKey ? () => {
      const cached = localStorage.getItem(fallbackKey);
      return cached ? JSON.parse(cached) : [];
    } : null;

    return queuedGet(endpoint, fallback, desc);
  },

  // Insert with fallback to localStorage
  insert: async (table, data, fallbackKey = null, description = null) => {
    const endpoint = `/rest/v1/${table}`;
    const desc = description || `INSERT into ${table}`;
    
    console.log(`🔍 INSERT operation: ${desc}`);
    console.log('Table:', table);
    console.log('Data:', JSON.stringify(data, null, 2));
    console.log('Endpoint:', endpoint);
    
    const fallback = fallbackKey ? () => {
      console.log(`💾 Storing to localStorage: ${fallbackKey}`);
      const existing = JSON.parse(localStorage.getItem(fallbackKey) || '[]');
      const updated = Array.isArray(data) ? [...existing, ...data] : [...existing, data];
      localStorage.setItem(fallbackKey, JSON.stringify(updated));
      return { success: true, fallback: true };
    } : null;

    try {
      const result = await queuedPost(endpoint, data, fallback, desc);
      console.log(`✅ INSERT successful:`, result);
      return result;
    } catch (error) {
      console.error(`❌ INSERT failed:`, error);
      throw error;
    }
  },

  // Update with fallback
  update: async (table, data, filter, fallbackFn = null, description = null) => {
    const endpoint = `/rest/v1/${table}?${filter}`;
    const desc = description || `UPDATE ${table}`;
    
    // Use a modified PATCH that returns representation to see if rows were affected
    return queuedSupabaseFetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Prefer': 'return=representation'  // Return the updated rows to see if any were affected
      }
    }, fallbackFn, desc);
  },

  // Delete with fallback
  delete: async (table, filter, fallbackFn = null, description = null) => {
    const endpoint = `/rest/v1/${table}?${filter}`;
    const desc = description || `DELETE from ${table}`;
    
    return queuedDelete(endpoint, fallbackFn, desc);
  },

  // Upsert (insert with conflict resolution)
  upsert: async (table, data, conflictColumn, fallbackKey = null, description = null) => {
    const endpoint = `/rest/v1/${table}`;
    const desc = description || `UPSERT into ${table}`;
    
    const fallback = fallbackKey ? () => {
      console.log(`💾 Storing to localStorage: ${fallbackKey}`);
      localStorage.setItem(fallbackKey, JSON.stringify(data));
      return { success: true, fallback: true };
    } : null;

    // Use POST with proper Supabase upsert parameters
    const queryParams = new URLSearchParams({
      on_conflict: conflictColumn  // This tells Supabase which column to use for conflict resolution
    });
    
    const fullEndpoint = `${endpoint}?${queryParams}`;
    
    return queuedSupabaseFetch(fullEndpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Prefer': 'resolution=merge-duplicates,return=minimal'  // Merge on conflict, don't return data
      }
    }, fallback, desc);
  }
};

export default queuedSupabaseOperation;
